import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { ConfirmDialog, EmptyState, SkeletonRow, Text } from '~/components/core';
import { Entypo } from '~/components/core/Icons';
import CustomHeader from '~/components/navigators/CustomHeader';
import ConfirmationMessage from '~/components/features/ConfirmationMessage';
import AIStrategyBuilderService from '~/api-services/AIStrategyBuilderService';
import { Strategy, StrategyResponse } from '~/api-services/AIStrategyBuilderService/types';
import { API_VERSION_NEWER_MESSAGE, API_VERSION_OLDER_MESSAGE, apiVersionState } from '~/constants/strategyConstants';
import { useAppSelector, useStyles } from '~/hooks';
import { symbolsSelector } from '~/state/selectors';
import i18n from '~/i18n';
import { FONT_SIZE_M, FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { strategyActionErrorMessage } from './utils/validation';
import { useStrategyProgress } from './hooks/useStrategyProgress';
import { useStrategyConsent } from './hooks/useStrategyConsent';
import { StrategyActionResult } from './hooks/useStrategies';
import QuickActionsSheet from './components/QuickActionsSheet';
import DecisionPanel from './components/detail/DecisionPanel';
import DetailTabs from './components/detail/DetailTabs';
import StrategyLive from './components/live/StrategyLive';
import FailureBody from './components/detail/FailureBody';
import ProgressBody from './components/detail/ProgressBody';
import ResultModeToggle, { ResultMode } from './components/detail/ResultModeToggle';
import ResultReveal from './components/detail/ResultReveal';
import StatusBanner from './components/detail/StatusBanner';
import { strategyTitleLine } from './utils/format';
import { getStrategyPreview } from './utils/previewCache';
import { strategyResultView } from './utils/results';

type Props = {
    strategyId: string;
    // Leave the screen entirely (used after archiving, which removes it from the default list).
    onExit: () => void;
    // Go to the strategy list. Deliberately not onExit: on mobile this detail is often the first
    // route on its stack, where goBack() bubbles to the tab navigator and lands on another tab.
    onOpenList: () => void;
    onClone: (strategyId: string) => void;
};

type PendingConfirm = 'enable' | 'disable' | 'discard' | 'archive' | null;

const CONFIRM_COPY: Record<Exclude<PendingConfirm, null>, { title: string; message: string; destructive: boolean }> = {
    enable: { title: 'ai-strategy-action-enable', message: 'ai-strategy-enable-confirm', destructive: false },
    disable: { title: 'ai-strategy-action-disable', message: 'ai-strategy-disable-confirm', destructive: false },
    discard: { title: 'ai-strategy-action-discard', message: 'ai-strategy-discard-confirm', destructive: true },
    archive: { title: 'ai-strategy-action-archive', message: 'ai-strategy-archive-confirm', destructive: false },
};

/**
 * One detail screen, six status-driven bodies (docs/ai-strategy/04-detail-view.md):
 * created/inprogress -> live progress; failed -> recovery; done -> full-screen result reveal
 * (Continue fires markSeen); seen -> result + decision; enabled/disabled -> banner + toggle;
 * discarded -> read-only. Result exploration happens in the top tabs (Statistics / Trades /
 * Chart / Setup) once the reveal has been passed.
 */
export default function StrategyDetails({ strategyId, onExit, onOpenList, onClone }: Props) {
    const styles = useStyles(stylesGenerator);

    // The tapped list row, captured once at mount, so the header shows the right identity before
    // the fetch lands. Absent on a cold load / deep link — then the header falls back to a skeleton.
    const [preview] = useState<Strategy | null>(() => getStrategyPreview(strategyId));

    const [strategy, setStrategy] = useState<Strategy | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionsOpen, setActionsOpen] = useState(false);
    const [confirm, setConfirm] = useState<PendingConfirm>(null);
    const { consentLabel, recordConsent } = useStrategyConsent();
    const [busy, setBusy] = useState(false);
    // `isError` left undefined is the neutral variant: ConfirmationMessage shows its "!" icon
    // rather than a success check, which is what an informational notice wants.
    const [toast, setToast] = useState<{ text: string; isError?: boolean } | null>(null);
    // Which results the area below the toggle describes. Seeded from the status once the strategy
    // loads — an active strategy opens on Live, since what it actually did is the more interesting
    // half — and left alone afterwards so it never overrides a switch the user made.
    const [resultMode, setResultMode] = useState<ResultMode>('backtest');
    const resultModeSeeded = useRef(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Every blob is opt-in since 2026-07-26 and this screen renders all of them:
            // Setup reads `input`, Statistics/Trades read `response`, the identity strip reads
            // `frontend_json` and the result headline reads `backend_json`.
            const { data } = await AIStrategyBuilderService.getStrategy(strategyId, {
                returnInputJSON: true,
                returnOutputJSON: true,
                returnFrontendJSON: true,
                returnBackendJSON: true,
            });
            if (data?.status && data?.returnData) {
                setStrategy(data.returnData);
            } else {
                setError((data as any)?.message || data?.errorCode || i18n.t('ai-strategy-detail-load-failed'));
            }
        } catch (err) {
            setError((err as any)?.message || i18n.t('ai-strategy-detail-load-failed'));
        } finally {
            setLoading(false);
        }
    }, [strategyId]);

    useEffect(() => {
        load();
    }, [load]);

    // Seeded once, when the strategy first arrives: an active strategy opens on Live. Guarded by
    // the ref so a later status change — or a re-render — cannot undo the user's own switch.
    useEffect(() => {
        if (resultModeSeeded.current || !strategy) {
            return;
        }
        resultModeSeeded.current = true;
        if (strategy.status === 'enabled') {
            setResultMode('live');
        }
    }, [strategy]);

    // Live progress while processing; reload the full strategy when it reaches a terminal state.
    // `load` above already fetched the persisted status/percent, so the hook seeds from that
    // instead of issuing a second GET for the same strategy.
    const progress = useStrategyProgress(strategyId, { seed: false, initial: strategy });
    useEffect(() => {
        if (!strategy || !progress.status) {
            return;
        }
        const wasProcessing = strategy.status === 'created' || strategy.status === 'inprogress';
        const nowTerminal = progress.status !== 'created' && progress.status !== 'inprogress';
        if (wasProcessing && nowTerminal && progress.status !== strategy.status) {
            load();
        }
    }, [progress.status, strategy, load]);

    const notify = useCallback((text: string, isError?: boolean) => setToast({ text, isError }), []);

    // Lifecycle actions applied to this screen's copy of the strategy (no list to patch here).
    const applyCall = useCallback(async (call: Promise<{ data: StrategyResponse }>): Promise<StrategyActionResult> => {
        try {
            const { data } = await call;
            if (data?.status && data.returnData) {
                setStrategy(data.returnData);
                return { ok: true, strategy: data.returnData };
            }
            return { ok: false, error: strategyActionErrorMessage(data) };
        } catch (err) {
            const data = (err as { response?: { data?: unknown } })?.response?.data;
            return { ok: false, error: strategyActionErrorMessage(data) };
        }
    }, []);

    const actions = {
        enable: (id: string) => applyCall(AIStrategyBuilderService.enableStrategy(id)),
        disable: (id: string) => applyCall(AIStrategyBuilderService.disableStrategy(id)),
        discard: (id: string) => applyCall(AIStrategyBuilderService.discardStrategy(id)),
        unarchive: (id: string) => applyCall(AIStrategyBuilderService.unarchiveStrategy(id)),
        archive: async (id: string): Promise<StrategyActionResult> => {
            const result = await applyCall(AIStrategyBuilderService.archiveStrategy(id));
            if (result.ok) {
                onExit();
            }
            return result;
        },
    };

    const runConfirmed = async (kind: Exclude<PendingConfirm, null>) => {
        if (kind === 'enable') {
            await recordConsent();
        }
        setBusy(true);
        const result = await actions[kind](strategyId);
        setBusy(false);
        setConfirm(null);
        if (result.ok) {
            notify(i18n.t('ai-strategy-updated'), false);
        } else {
            notify(result.error ?? 'Action failed', true);
        }
    };

    const cloneThis = useCallback(() => onClone(strategyId), [onClone, strategyId]);

    // Continue from the result reveal: done -> seen (per the lifecycle spec), which drops the
    // screen into the regular decision body. Marking seen is the user's explicit choice here —
    // it never fires just from opening the screen.
    const proceedFromReveal = useCallback(async () => {
        setBusy(true);
        const result = await applyCall(AIStrategyBuilderService.markSeen(strategyId));
        setBusy(false);
        if (!result.ok) {
            notify(result.error ?? 'Action failed', true);
        }
    }, [applyCall, notify, strategyId]);

    const handleBack = useCallback(() => onOpenList(), [onOpenList]);

    // Identity for the header: the loaded strategy once it arrives, otherwise the list preview so
    // the title is right from the first frame. The body never reads this — only the header does.
    const headerStrategy = strategy ?? preview ?? null;
    const instrumentName =
        useAppSelector((state) =>
            headerStrategy ? symbolsSelector(state)[headerStrategy.instrument_code]?.nameTranslated : undefined
        ) ??
        headerStrategy?.instrument_code ??
        '';
    // The full identity line including TP/SL ("Gold · Buy · 1 hour · TP 30 · SL 5"). Plain
    // placeholder until the fetch (and any list preview) resolve on a cold load / deep link.
    const headerTitle = headerStrategy
        ? strategyTitleLine(headerStrategy, instrumentName)
        : i18n.t('ai-strategy-detail-title');

    // CustomHeader as elsewhere, but the detail's left-aligned variant: a chevron-only back (no
    // "back" word) and a smaller title font hand the full TP/SL line the room it needs — a centred,
    // full-size title would overrun the actions. Back routes to the list; the "..." shows once the
    // full strategy has loaded (a preview alone has nothing to act on yet).
    const header = (
        <CustomHeader
            title={headerTitle}
            noTranslateTitle
            titleAlign="left"
            hideBackLabel
            titleFontSize={FONT_SIZE_M}
            titleNumberOfLines={1}
            onBack={handleBack}
            right={
                strategy ? (
                    <Pressable
                        hitSlop={10}
                        onPress={() => setActionsOpen(true)}
                        style={styles.headerButton}
                        accessibilityLabel="Actions"
                    >
                        <Entypo name="dots-three-horizontal" size={scaleSize(18)} style={styles.headerIcon} />
                    </Pressable>
                ) : undefined
            }
        />
    );

    if (loading && !strategy) {
        return (
            <>
                {header}
                <View style={styles.skeletons}>
                    <SkeletonRow height={18} width="60%" />
                    <SkeletonRow height={12} width="40%" />
                    <SkeletonRow height={120} width="100%" borderRadius={10} />
                </View>
            </>
        );
    }

    if (!strategy) {
        return (
            <>
                {header}
                <EmptyState
                    title="ai-strategy-detail-load-failed"
                    body={error ?? undefined}
                    ctaLabel="retry"
                    onCtaPress={load}
                />
            </>
        );
    }

    const status = strategy.status;
    const processing = status === 'created' || status === 'inprogress';
    // A finished-but-unseen result gets the full-screen reveal first; the decision body (panel
    // + tabs) is reached by passing it. Discarding from the reveal skips the decision entirely.
    const revealing = status === 'done';
    const deciding = status === 'seen';
    const settled = status === 'enabled' || status === 'disabled' || status === 'discarded';
    const hasLiveSide = status === 'enabled' || status === 'disabled';
    const results = strategyResultView(strategy);
    const versionState = apiVersionState(strategy.api_version);
    const canClone = versionState === 'current';

    return (
        <View style={styles.container}>
            {header}
            {versionState !== 'current' ? (
                <Text noTranslate style={styles.versionWarning}>
                    {versionState === 'older' ? API_VERSION_OLDER_MESSAGE : API_VERSION_NEWER_MESSAGE}
                </Text>
            ) : null}

            {processing ? (
                <ProgressBody percent={progress.percent} queued={status === 'created' && !progress.percent} />
            ) : null}

            {status === 'failed' ? (
                <FailureBody canClone={canClone} onClone={cloneThis} onArchive={() => setConfirm('archive')} />
            ) : null}

            {revealing ? (
                <ResultReveal
                    results={results}
                    busy={busy}
                    onContinue={proceedFromReveal}
                    onDiscard={() => setConfirm('discard')}
                />
            ) : null}

            {deciding ? (
                <DecisionPanel
                    results={results}
                    busy={busy}
                    onEnable={() => setConfirm('enable')}
                    onDiscard={() => setConfirm('discard')}
                />
            ) : null}

            {settled ? (
                <StatusBanner
                    status={status as 'enabled' | 'disabled' | 'discarded'}
                    actionLabel={
                        status === 'enabled'
                            ? i18n.t('ai-strategy-action-disable')
                            : status === 'disabled'
                            ? i18n.t('ai-strategy-action-enable')
                            : i18n.t('ai-strategy-action-clone')
                    }
                    onAction={
                        status === 'enabled'
                            ? () => setConfirm('disable')
                            : status === 'disabled'
                            ? () => setConfirm('enable')
                            : canClone
                            ? cloneThis
                            : undefined
                    }
                />
            ) : null}

            {/* Only a strategy that has traded has a live side to offer: `disabled` reaches that
                state from `enabled`, while `seen` and `discarded` never went live at all. */}
            {hasLiveSide ? <ResultModeToggle mode={resultMode} onChange={setResultMode} /> : null}

            {/* Both sides render their own empty states when data is genuinely missing. */}
            {deciding || settled ? (
                <View style={styles.tabsWrap}>
                    {hasLiveSide && resultMode === 'live' ? (
                        <StrategyLive strategy={strategy} />
                    ) : (
                        <DetailTabs strategy={strategy} />
                    )}
                </View>
            ) : null}

            <QuickActionsSheet
                strategy={actionsOpen ? strategy : null}
                onClose={() => setActionsOpen(false)}
                onClone={() => {
                    setActionsOpen(false);
                    cloneThis();
                }}
                actions={actions}
                notify={notify}
            />

            {confirm ? (
                <ConfirmDialog
                    visible
                    title={CONFIRM_COPY[confirm].title}
                    message={CONFIRM_COPY[confirm].message}
                    destructive={CONFIRM_COPY[confirm].destructive}
                    consentLabel={confirm === 'enable' ? consentLabel : undefined}
                    onConfirm={() => (busy ? undefined : runConfirmed(confirm))}
                    onCancel={() => setConfirm(null)}
                />
            ) : null}

            {toast ? (
                <ConfirmationMessage
                    visible
                    text={toast.text}
                    error={toast.isError}
                    setVisible={() => setToast(null)}
                />
            ) : null}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: { flex: 1 },
        skeletons: {
            padding: scaleSize(14),
        },
        versionWarning: {
            color: theme.alert,
            fontSize: FONT_SIZE_XS,
            textAlign: 'center',
            marginHorizontal: scaleSize(12),
            marginBottom: scaleSize(6),
        },
        headerButton: {
            paddingHorizontal: scaleSize(8),
        },
        headerIcon: {
            color: theme.text,
        },
        tabsWrap: {
            flex: 1,
        },
    });
